import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Paper,
  Divider,
  Switch,
  FormControlLabel,
  Slider,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  Zoom,
  LinearProgress
} from '@mui/material';
import {
  CheckCircleRounded,
  StarRounded,
  TrendingUpRounded,
  CalculateRounded,
  InfoRounded,
  ExpandMoreRounded,
  RocketLaunchRounded,
  BusinessRounded,
  AccountCircleRounded,
  SupportAgentRounded,
  SecurityRounded,
  SpeedRounded,
  CloudRounded,
  AnalyticsRounded,
  CampaignRounded,
  EmailRounded,
  ForumRounded,
  SellRounded,
  SportsEsportsRounded,
  AutoAwesomeRounded,
  FlashOnRounded,
  ShieldRounded,
  MonetizationOnRounded,
  SavingsRounded,
  CompareArrowsRounded,
  EditNotifications
} from '@mui/icons-material';

const PricingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isYearly, setIsYearly] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(1);
  const [customModules, setCustomModules] = useState([]);
  const [roiInputs, setRoiInputs] = useState({
    currentVisitors: 10000,
    conversionRate: 2.5,
    averageRevenue: 50
  });
  const [expandedFaq, setExpandedFaq] = useState(false);
  const heroRef = useRef(null);
  const [isHeroVisible, setIsHeroVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsHeroVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    if (heroRef.current) observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, []);

  const packages = [
    {
      id: 0,
      name: 'Başlangıç',
      subtitle: 'Küçük haber siteleri için',
      monthlyPrice: 299,
      yearlyPrice: 2990,
      color: '#059669',
      popular: false,
      modules: [
        'Temel haber yönetimi',
        '30 widget erişimi',
        '5 kullanıcı',
        'Mobil responsive',
        'E-posta desteği',
        'Temel analitik'
      ],
      limits: {
        visitors: '50K/ay',
        storage: '10GB',
        support: 'E-posta'
      },
      features: [
        'SSL Sertifikası',
        'Temel SEO araçları',
        'Sosyal medya entegrasyonu',
        'Haftalık yedekleme',
        'Temel widget kütüphanesi',
        'Mobil uyumlu tasarım'
      ]
    },
    {
      id: 1,
      name: 'Profesyonel',
      subtitle: 'Orta ölçekli haber platformları için',
      monthlyPrice: 599,
      yearlyPrice: 5990,
      color: '#2563eb',
      popular: true,
      modules: [
        'Gelişmiş haber yönetimi',
        '60 widget erişimi',
        '20 kullanıcı',
        'Newsletter sistemi',
        'Öncelikli destek',
        'Online eğitim'
      ],
      limits: {
        visitors: '250K/ay',
        storage: '50GB',
        support: 'Öncelikli'
      },
      features: [
        'Gelişmiş analitik',
        'RSS entegrasyonu',
        'CDN desteği',
        'Günlük yedekleme',
        'API erişimi',
        'Çoklu yazar desteği',
        'Gelişmiş SEO araçları',
        'Haber ajansı entegrasyonu'
      ]
    },
    {
      id: 2,
      name: 'Kurumsal',
      subtitle: 'Büyük haber kuruluşları için',
      monthlyPrice: 1299,
      yearlyPrice: 12990,
      color: '#7c3aed',
      popular: false,
      modules: [
        'Tüm özellikler',
        '70+ widget (tümü)',
        'Sınırsız kullanıcı',
        'Abonelik sistemi',
        'BİK uyumluluğu',
        'Özel geliştirme'
      ],
      limits: {
        visitors: 'Sınırsız',
        storage: 'Sınırsız',
        support: 'Özel Destek Ekibi'
      },
      features: [
        'Özel destek ekibi',
        'Kurulum ve eğitim',
        'Premium analitik',
        'API erişimi',
        'SLA garantisi',
        'Özel entegrasyonlar',
        'Gerçek zamanlı yedekleme',
        'Multi-site yönetimi',
        'Özel sunucu seçeneği'
      ]
    }
  ];

  const additionalModules = [
    {
      name: 'Anket Modülü',
      price: 99,
      description: 'Çoktan seçmeli anketler, evet/hayır anketleri ve anket arşivi',
      icon: <AnalyticsRounded />,
      color: '#3b82f6'
    },
    {
      name: 'Blog Modülü',
      price: 149,
      description: 'Blog yazıları, yazar profilleri ve yorum sistemi',
      icon: <EditNotifications />,
      color: '#8b5cf6'
    },
    {
      name: 'Forum Modülü',
      price: 199,
      description: 'Tartışma konuları, kullanıcı etkileşimi ve moderasyon araçları',
      icon: <ForumRounded />,
      color: '#16a34a'
    },
    {
      name: 'İlan Modülü',
      price: 249,
      description: 'Sınıflandırılmış ilanlar, ilan kategorileri ve ücretli ilan sistemi',
      icon: <SellRounded />,
      color: '#ca8a04'
    },
    {
      name: 'Kampanya Modülü',
      price: 179,
      description: 'Kampanya yönetimi, banner yönetimi ve hedefleme seçenekleri',
      icon: <CampaignRounded />,
      color: '#dc2626'
    },
    {
      name: 'Hava Durumu Servisi',
      price: 99,
      description: '4 farklı tasarım, şehir bazlı hava durumu ve 5 günlük tahmin',
      icon: <CloudRounded />,
      color: '#0ea5e9'
    }
  ];

  const faqData = [
    {
      question: 'Paket değişikliği nasıl yapılır?',
      answer: 'Paket değişikliği istediğiniz zaman yapılabilir. Üst pakete geçiş anında aktif olurken, alt pakete geçiş sonraki fatura döneminde geçerli olur. Fark ücreti pro-rata olarak hesaplanır.'
    },
    {
      question: 'Kurulum süreci ne kadar sürer?',
      answer: 'Başlangıç paketi 24 saat, Profesyonel paket 48 saat, Enterprise paket ise 72 saat içinde kurulur. Özel geliştirmeler için süre değişkenlik gösterebilir.'
    },
    {
      question: 'Veri güvenliği nasıl sağlanıyor?',
      answer: 'Tüm verileriniz SSL ile şifrelenir, günlük yedekleme yapılır ve ISO 27001 sertifikalı veri merkezlerinde saklanır. KVKK uyumluluğu garanti edilir.'
    },
    {
      question: 'Özel modül geliştirme mümkün mü?',
      answer: 'Evet, Enterprise paket ile birlikte özel modül geliştirme hizmeti sunuyoruz. Geliştirme süresi ve maliyeti projenin kapsamına göre belirlenir.'
    },
    {
      question: 'Destek hizmetleri neler içeriyor?',
      answer: 'Başlangıç paketinde email destek, Profesyonel paketinde 7/24 chat destek, Enterprise paketinde ise özel temsilci ataması bulunur. Tüm paketlerde dokümantasyon ve video eğitimler mevcuttur.'
    },
    {
      question: 'İptal işlemi nasıl yapılır?',
      answer: 'İptal işlemi 30 gün önceden bildirim ile yapılır. Mevcut dönem sonuna kadar hizmet devam eder. İlk 30 gün içinde %100 para iade garantisi vardır.'
    }
  ];

  const handlePackageSelect = (packageId) => {
    setSelectedPackage(packageId);
  };

  const handleModuleToggle = (module) => {
    setCustomModules(prev => 
      prev.includes(module.name)
        ? prev.filter(m => m !== module.name)
        : [...prev, module.name]
    );
  };

  const calculateROI = () => {
    const { currentVisitors, conversionRate, averageRevenue } = roiInputs;
    const monthlyRevenue = (currentVisitors * (conversionRate / 100) * averageRevenue);
    const yearlyRevenue = monthlyRevenue * 12;
    const packageCost = packages[selectedPackage][isYearly ? 'yearlyPrice' : 'monthlyPrice'] * (isYearly ? 1 : 12);
    const additionalCost = customModules.reduce((sum, moduleName) => {
      const module = additionalModules.find(m => m.name === moduleName);
      return sum + (module ? module.price * (isYearly ? 10 : 12) : 0);
    }, 0);
    const totalCost = packageCost + additionalCost;
    const roi = ((yearlyRevenue - totalCost) / totalCost * 100);
    const paybackPeriod = totalCost / monthlyRevenue;

    return {
      monthlyRevenue,
      yearlyRevenue,
      totalCost,
      roi: Math.max(0, roi),
      paybackPeriod: Math.max(0, paybackPeriod)
    };
  };

  const roiData = calculateROI();

  const handleFaqExpand = (panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        ref={heroRef}
        sx={{
          minHeight: '60vh',
          background: `
            linear-gradient(135deg, #0f0f23 0%, #1a1a2e 25%, #16213e 50%, #0f3460 75%, #0e4b99 100%),
            radial-gradient(circle at 30% 40%, rgba(120, 119, 198, 0.4) 0%, transparent 50%),
            radial-gradient(circle at 70% 60%, rgba(255, 119, 198, 0.3) 0%, transparent 50%)
          `,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              repeating-linear-gradient(
                90deg,
                transparent,
                transparent 98px,
                rgba(255, 255, 255, 0.03) 100px
              )
            `,
            zIndex: 1
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={8} mx="auto" textAlign="center">
              <Fade in={isHeroVisible} timeout={1000}>
                <Box>
                  <Chip
                    label="💰 FİYATLANDIRMA"
                    sx={{
                      mb: 3,
                      background: 'linear-gradient(45deg, #f59e0b, #ef4444)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      px: 2,
                      py: 0.5,
                      animation: 'pulse 2s infinite'
                    }}
                  />
                  
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: '2.5rem', md: '4rem' },
                      fontWeight: 800,
                      background: 'linear-gradient(45deg, #ffffff, #a78bfa, #60a5fa)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 3,
                      lineHeight: 1.2
                    }}
                  >
                    Şeffaf ve
                    <br />
                    <span style={{ 
                      background: 'linear-gradient(45deg, #10b981, #06b6d4)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}>
                      Esnek Fiyatlar
                    </span>
                  </Typography>

                  {/* Pricing Toggle */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 4,
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '50px',
                      p: 1,
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      maxWidth: '300px',
                      mx: 'auto'
                    }}
                  >
                    <Typography 
                      sx={{ 
                        color: !isYearly ? 'white' : 'rgba(255, 255, 255, 0.6)',
                        fontWeight: 600,
                        mr: 2
                      }}
                    >
                      Aylık
                    </Typography>
                    <Switch
                      checked={isYearly}
                      onChange={(e) => setIsYearly(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-thumb': {
                          background: 'linear-gradient(45deg, #10b981, #06b6d4)'
                        },
                        '& .MuiSwitch-track': {
                          backgroundColor: 'rgba(255, 255, 255, 0.3)'
                        }
                      }}
                    />
                    <Typography 
                      sx={{ 
                        color: isYearly ? 'white' : 'rgba(255, 255, 255, 0.6)',
                        fontWeight: 600,
                        ml: 2
                      }}
                    >
                      Yıllık
                    </Typography>
                    {isYearly && (
                      <Chip
                        label="%20 İndirim"
                        size="small"
                        sx={{
                          ml: 2,
                          background: 'linear-gradient(45deg, #10b981, #06b6d4)',
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    )}
                  </Box>

                  {/* <Stack direction="row" spacing={2} justifyContent="center">
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<CalculateRounded />}
                      sx={{
                        background: 'linear-gradient(45deg, #10b981, #06b6d4)',
                        color: 'white',
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        borderRadius: '50px',
                        boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 40px rgba(16, 185, 129, 0.4)'
                        }
                      }}
                      onClick={() => document.getElementById('roi-calculator').scrollIntoView({ behavior: 'smooth' })}
                    >
                      ROI Hesapla
                    </Button>
                  </Stack> */}
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Container>

        <style jsx>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.8; transform: scale(1.05); }
          }
        `}</style>
      </Box>

      {/* Pricing Packages */}
      <Box sx={{ py: 8, background: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Chip
              label="📦 PAKET SEÇENEKLERİ"
              sx={{
                mb: 3,
                background: 'linear-gradient(45deg, #4f46e5, #06b6d4)',
                color: 'white',
                fontWeight: 600,
                px: 2
              }}
            />
            <Typography variant="h3" fontWeight={700} color="#1e293b" mb={2}>
              Size Uygun Paketi Seçin
            </Typography>
            <Typography variant="h6" color="#64748b" maxWidth="600px" mx="auto">
              Küçük bloglardan büyük medya kuruluşlarına kadar her ihtiyaca uygun çözümler
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {packages.map((pkg, index) => (
              <Grid item xs={12} md={4} key={pkg.id}>
                <Zoom in timeout={300 + index * 200}>
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: 4,
                      border: pkg.popular ? `3px solid ${pkg.color}` : '1px solid rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      position: 'relative',
                      background: selectedPackage === pkg.id 
                        ? `linear-gradient(135deg, ${pkg.color}10, ${pkg.color}05)` 
                        : 'white',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 20px 40px ${pkg.color}20`,
                        borderColor: pkg.color
                      }
                    }}
                    onClick={() => handlePackageSelect(pkg.id)}
                  >
                    {pkg.popular && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -15,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: `linear-gradient(45deg, ${pkg.color}, ${pkg.color}dd)`,
                          color: 'white',
                          px: 3,
                          py: 1,
                          borderRadius: '20px',
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          zIndex: 1
                        }}
                      >
                        <StarRounded fontSize="small" />
                        EN POPÜLER
                      </Box>
                    )}

                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                      <Avatar
                        sx={{
                          width: 80,
                          height: 80,
                          mx: 'auto',
                          mb: 2,
                          background: `linear-gradient(45deg, ${pkg.color}, ${pkg.color}dd)`,
                          fontSize: '2rem'
                        }}
                      >
                        {index === 0 && <AccountCircleRounded sx={{ fontSize: '2rem' }} />}
                        {index === 1 && <BusinessRounded sx={{ fontSize: '2rem' }} />}
                        {index === 2 && <RocketLaunchRounded sx={{ fontSize: '2rem' }} />}
                      </Avatar>

                      <Typography variant="h4" fontWeight={700} color="#1e293b" mb={1}>
                        {pkg.name}
                      </Typography>
                      
                      <Typography variant="body1" color="#64748b" mb={3}>
                        {pkg.subtitle}
                      </Typography>

                      <Box mb={3}>
                        <Typography
                          variant="h2"
                          fontWeight={800}
                          color={pkg.color}
                          component="span"
                        >
                          ₺{isYearly ? pkg.yearlyPrice : pkg.monthlyPrice}
                        </Typography>
                        <Typography variant="body1" color="#64748b" component="span">
                          /{isYearly ? 'yıl' : 'ay'}
                        </Typography>
                        {isYearly && (
                          <Typography variant="body2" color="#10b981" fontWeight={600}>
                            ₺{(pkg.monthlyPrice * 12) - pkg.yearlyPrice} tasarruf!
                          </Typography>
                        )}
                      </Box>

                      <Button
                        variant={selectedPackage === pkg.id ? "contained" : "outlined"}
                        fullWidth
                        size="large"
                        sx={{
                          mb: 3,
                          py: 1.5,
                          borderRadius: '50px',
                          fontWeight: 600,
                          ...(selectedPackage === pkg.id ? {
                            background: `linear-gradient(45deg, ${pkg.color}, ${pkg.color}dd)`,
                            color: 'white'
                          } : {
                            borderColor: pkg.color,
                            color: pkg.color,
                            '&:hover': {
                              backgroundColor: `${pkg.color}10`
                            }
                          })
                        }}
                        onClick={() => handlePackageSelect(pkg.id)}
                      >
                        {selectedPackage === pkg.id ? 'Seçildi' : 'Bu Paketi Seç'}
                      </Button>

                      <Divider sx={{ mb: 3 }} />

                      {/* Package Limits */}
                      <Box mb={3}>
                        <Typography variant="subtitle2" fontWeight={600} color="#1e293b" mb={2}>
                          Limitler:
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={4}>
                            <Box textAlign="center">
                              <Typography variant="h6" fontWeight={600} color={pkg.color}>
                                {pkg.limits.visitors}
                              </Typography>
                              <Typography variant="caption" color="#64748b">
                                Ziyaretçi
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={4}>
                            <Box textAlign="center">
                              <Typography variant="h6" fontWeight={600} color={pkg.color}>
                                {pkg.limits.storage}
                              </Typography>
                              <Typography variant="caption" color="#64748b">
                                Depolama
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={4}>
                            <Box textAlign="center">
                              <Typography variant="h6" fontWeight={600} color={pkg.color}>
                                {pkg.limits.support}
                              </Typography>
                              <Typography variant="caption" color="#64748b">
                                Destek
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>

                      <Divider sx={{ mb: 3 }} />

                      {/* Package Modules */}
                      <Box textAlign="left" mb={3}>
                        <Typography variant="subtitle2" fontWeight={600} color="#1e293b" mb={2}>
                          Dahil Modüller:
                        </Typography>
                        {pkg.modules.map((module, idx) => (
                          <Box key={idx} display="flex" alignItems="center" mb={1}>
                            <CheckCircleRounded sx={{ fontSize: 16, color: pkg.color, mr: 1 }} />
                            <Typography variant="body2" color="#64748b">
                              {module}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      <Divider sx={{ mb: 3 }} />

                      {/* Additional Features */}
                      <Box textAlign="left">
                        <Typography variant="subtitle2" fontWeight={600} color="#1e293b" mb={2}>
                          Ek Özellikler:
                        </Typography>
                        {pkg.features.map((feature, idx) => (
                          <Box key={idx} display="flex" alignItems="center" mb={1}>
                            <CheckCircleRounded sx={{ fontSize: 16, color: pkg.color, mr: 1 }} />
                            <Typography variant="body2" color="#64748b">
                              {feature}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Additional Modules */}
      <Box sx={{ py: 8, background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Chip
              label="🔧 EK MODÜLLER"
              sx={{
                mb: 3,
                background: 'linear-gradient(45deg, #f59e0b, #ef4444)',
                color: 'white',
                fontWeight: 600,
                px: 2
              }}
            />
            <Typography variant="h3" fontWeight={700} color="white" mb={2}>
              İstediğiniz Modülleri Ekleyin
            </Typography>
            <Typography variant="h6" color="rgba(255, 255, 255, 0.7)" maxWidth="600px" mx="auto">
              Paketinizi özelleştirin ve sadece ihtiyacınız olan modüller için ödeme yapın
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {additionalModules.map((module, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Fade in timeout={400 + index * 100}>
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: 4,
                      background: customModules.includes(module.name)
                        ? `linear-gradient(135deg, ${module.color}20, ${module.color}10)`
                        : 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(10px)',
                      border: customModules.includes(module.name)
                        ? `2px solid ${module.color}`
                        : '1px solid rgba(255, 255, 255, 0.1)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: `0 15px 35px ${module.color}30`,
                        borderColor: module.color
                      }
                    }}
                    onClick={() => handleModuleToggle(module)}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                        <Avatar
                          sx={{
                            background: `linear-gradient(45deg, ${module.color}, ${module.color}dd)`,
                            width: 48,
                            height: 48
                          }}
                        >
                          {module.icon}
                        </Avatar>
                        <Box textAlign="right">
                          <Typography variant="h5" fontWeight={700} color="white">
                            ₺{module.price}
                          </Typography>
                          <Typography variant="caption" color="rgba(255, 255, 255, 0.7)">
                            /ay
                          </Typography>
                        </Box>
                      </Box>

                      <Typography variant="h6" fontWeight={600} color="white" mb={1}>
                        {module.name}
                      </Typography>
                      
                      <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" mb={3}>
                        {module.description}
                      </Typography>

                      <Button
                        variant={customModules.includes(module.name) ? "contained" : "outlined"}
                        fullWidth
                        sx={{
                          borderRadius: '25px',
                          py: 1,
                          fontWeight: 600,
                          ...(customModules.includes(module.name) ? {
                            background: `linear-gradient(45deg, ${module.color}, ${module.color}dd)`,
                            color: 'white'
                          } : {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                            color: 'white',
                            '&:hover': {
                              borderColor: module.color,
                              backgroundColor: `${module.color}20`
                            }
                          })
                        }}
                      >
                        {customModules.includes(module.name) ? 'Eklendi' : 'Modülü Ekle'}
                      </Button>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ROI Calculator */}
      <Box 
        id="roi-calculator"
        sx={{ py: 8, background: 'linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%)' }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Chip
              label="📊 ROI HESAPLAYICI"
              sx={{
                mb: 3,
                background: 'linear-gradient(45deg, #059669, #0891b2)',
                color: 'white',
                fontWeight: 600,
                px: 2
              }}
            />
            <Typography variant="h3" fontWeight={700} color="#1e293b" mb={2}>
              Yatırımınızın Geri Dönüşünü Hesaplayın
            </Typography>
            <Typography variant="h6" color="#64748b" maxWidth="600px" mx="auto">
              Mevcut verilerinizi girin ve sistemimizin size kazandıracağını görün
            </Typography>
          </Box>

          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 4, borderRadius: 4 }}>
                <Typography variant="h5" fontWeight={600} color="#1e293b" mb={3}>
                  Site Verilerinizi Girin
                </Typography>

                <Box mb={3}>
                  <Typography variant="subtitle1" fontWeight={600} color="#1e293b" mb={1}>
                    Aylık Ziyaretçi Sayısı
                  </Typography>
                  <Slider
                    value={roiInputs.currentVisitors}
                    onChange={(e, value) => setRoiInputs(prev => ({ ...prev, currentVisitors: value }))}
                    min={1000}
                    max={1000000}
                    step={1000}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${(value / 1000).toFixed(0)}K`}
                    sx={{
                      color: '#059669',
                      '& .MuiSlider-thumb': {
                        background: 'linear-gradient(45deg, #059669, #0891b2)'
                      }
                    }}
                  />
                  <Typography variant="body2" color="#64748b">
                    Mevcut: {(roiInputs.currentVisitors / 1000).toFixed(0)}K ziyaretçi/ay
                  </Typography>
                </Box>

                <Box mb={3}>
                  <Typography variant="subtitle1" fontWeight={600} color="#1e293b" mb={1}>
                    Dönüşüm Oranı (%)
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={roiInputs.conversionRate}
                    onChange={(e) => setRoiInputs(prev => ({ ...prev, conversionRate: parseFloat(e.target.value) || 0 }))}
                    InputProps={{
                      endAdornment: '%'
                    }}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" color="#64748b">
                    Ziyaretçilerin kaçı müşteri oluyor?
                  </Typography>
                </Box>

                <Box mb={3}>
                  <Typography variant="subtitle1" fontWeight={600} color="#1e293b" mb={1}>
                    Ortalama Müşteri Değeri (₺)
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={roiInputs.averageRevenue}
                    onChange={(e) => setRoiInputs(prev => ({ ...prev, averageRevenue: parseFloat(e.target.value) || 0 }))}
                    InputProps={{
                      startAdornment: '₺'
                    }}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" color="#64748b">
                    Bir müşteriden ortalama ne kadar kazanıyorsunuz?
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper 
                sx={{ 
                  p: 4, 
                  borderRadius: 4, 
                  background: 'linear-gradient(135deg, #059669 0%, #0891b2 100%)',
                  color: 'white'
                }}
              >
                <Typography variant="h5" fontWeight={600} mb={3}>
                   Analiz
                </Typography>

                <Grid container spacing={3} mb={3}>
                  <Grid item xs={6}>
                    <Box textAlign="center" p={2} sx={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: 2 }}>
                      <MonetizationOnRounded sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4" fontWeight={700}>
                        ₺{roiData.monthlyRevenue.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Aylık Gelir
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box textAlign="center" p={2} sx={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: 2 }}>
                      <SavingsRounded sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4" fontWeight={700}>
                        ₺{roiData.yearlyRevenue.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Yıllık Gelir
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Box mb={3}>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>
                    Toplam Yatırım Maliyeti
                  </Typography>
                  <Typography variant="h4" fontWeight={700} mb={1}>
                    ₺{roiData.totalCost.toLocaleString('tr-TR')}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {packages[selectedPackage].name} paketi + {customModules.length} ek modül
                  </Typography>
                </Box>

                <Box mb={3}>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>
                    ROI (Yatırım Getirisi)
                  </Typography>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Typography variant="h3" fontWeight={700} mr={2}>
                      %{roiData.roi.toFixed(0)}
                    </Typography>
                    <TrendingUpRounded sx={{ fontSize: 30 }} />
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min(roiData.roi, 100)} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#10b981'
                      }
                    }} 
                  />
                </Box>

                <Box>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>
                    Geri Ödeme Süresi
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {roiData.paybackPeriod.toFixed(1)} ay
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Yatırımınız bu sürede kendini amorti eder
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 8, background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
        <Container maxWidth="md">
          <Box textAlign="center" mb={6}>
            <Chip
              label="❓ SSS"
              sx={{
                mb: 3,
                background: 'linear-gradient(45deg, #7c3aed, #a855f7)',
                color: 'white',
                fontWeight: 600,
                px: 2
              }}
            />
            <Typography variant="h3" fontWeight={700} color="#1e293b" mb={2}>
              Sıkça Sorulan Sorular
            </Typography>
            <Typography variant="h6" color="#64748b">
              Merak ettiğiniz her şey burada
            </Typography>
          </Box>

          {faqData.map((faq, index) => (
            <Accordion
              key={index}
              expanded={expandedFaq === index}
              onChange={handleFaqExpand(index)}
              sx={{
                mb: 2,
                borderRadius: 2,
                border: '1px solid rgba(0, 0, 0, 0.1)',
                '&:before': { display: 'none' },
                boxShadow: expandedFaq === index ? '0 8px 25px rgba(0, 0, 0, 0.1)' : 'none'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreRounded />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center'
                  }
                }}
              >
                <Typography variant="h6" fontWeight={600} color="#1e293b">
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" color="#64748b" lineHeight={1.7}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: 8,
          background: `
            linear-gradient(135deg, #0f0f23 0%, #1a1a2e 25%, #16213e 50%, #0f3460 75%, #0e4b99 100%),
            radial-gradient(circle at 30% 40%, rgba(120, 119, 198, 0.4) 0%, transparent 50%),
            radial-gradient(circle at 70% 60%, rgba(255, 119, 198, 0.3) 0%, transparent 50%)
          `,
          position: 'relative',
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            fontWeight={700}
            sx={{
              background: 'linear-gradient(45deg, #ffffff, #a78bfa)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 3
            }}
          >
            Hemen Başlayın!
          </Typography>
          
          <Typography
            variant="h6"
            sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 4, lineHeight: 1.6 }}
          >
            {packages[selectedPackage].name} paketi ile haber sitenizi kurmaya başlayın. 
            İlk 30 gün %100 para iade garantisi!
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              startIcon={<RocketLaunchRounded />}
              sx={{
                background: 'linear-gradient(45deg, #10b981, #06b6d4)',
                color: 'white',
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 600,
                borderRadius: '50px',
                boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(16, 185, 129, 0.4)'
                }
              }}
            >
              {packages[selectedPackage].name} ile Başla
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              startIcon={<SupportAgentRounded />}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: 'white',
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                borderRadius: '50px',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Uzmanla Konuş
            </Button>
          </Stack>

          <Box mt={4}>
            <Typography variant="body2" color="rgba(255, 255, 255, 0.6)" mb={2}>
              Seçili Paket: {packages[selectedPackage].name} - 
              ₺{isYearly ? packages[selectedPackage].yearlyPrice : packages[selectedPackage].monthlyPrice}
              /{isYearly ? 'yıl' : 'ay'}
            </Typography>
            {customModules.length > 0 && (
              <Typography variant="body2" color="rgba(255, 255, 255, 0.6)">
                Ek Modüller: {customModules.join(', ')} - 
                ₺{customModules.reduce((sum, moduleName) => {
                  const module = additionalModules.find(m => m.name === moduleName);
                  return sum + (module ? module.price : 0);
                }, 0)} ek/ay
              </Typography>
            )}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default PricingPage;
